import { OrganizationBase } from './OrganizationBase'
import ClientBase from './ClientBase'

export enum TransferStatus {
  DRAFT = 'draft',
  PENDING_DOCUMENTS = 'pending_documents',
  PENDING_PAYMENT = 'pending_payment',
  PAID = 'paid',
  SENT_TO_TRANSFIERO = 'sent',
  IN_PROCESS = 'in_process',
  SIGNATURE_READY = 'signature_ready',
  NOTARIZED = 'notarized',
  REGISTERED = 'registered',
  REJECTED = 'rejected',
  VOIDED = 'voided',
}

export enum TransferType {
  STANDARD = 'standard',
  EXPRESS = 'express',
}

export interface TransferVehicleData {
  plate: string
  brand: string
  model: string
  year: number
  vin?: string
  chassis?: string
  engineNum?: string
  color?: string
}

export interface LegalEntity {
  companyName: string
  companyRut: string
  repName: string
  repRut: string
  address?: string
  powerDate: string
  documentType: string
  notaryOffice: string
  notaryName: string
  powerDocumentS3Key?: string
}

export interface TransferParty {
  type: 'Natural' | 'Legal'
  name: string
  rut: string
  address?: string
  legalEntity?: LegalEntity
}

export interface CompraPara {
  enabled: boolean
  ownerName: string
  ownerRut: string
}

export interface TransferDocuments {
  /**
   * Natural: Buyer's ID (Front)
   * Legal: Buyer Company's E-RUT (Frontal)
   */
  frontBuyerId?: string

  /**
   * Natural: Buyer's ID (Back)
   * Legal: Legal Representative's ID (Frontal)
   */
  backBuyerId?: string

  /**
   * Natural: Seller's ID (Front)
   * Legal: Seller Company's E-RUT (Frontal)
   */
  frontSellerId?: string

  /**
   * Natural: Seller's ID (Back)
   * Legal: Legal Representative's ID (Frontal)
   */
  backSellerId?: string
  backSellerRepId?: string

  circulationPermit?: string
  draftContract?: string
  notarizedContract?: string
  powerDocument?: string
}

export interface DocumentValidationCheck {
  field: string
  expected: string
  found: boolean
  matchedLine?: string
  extractedValue?: string
  actionable?: boolean
}

export interface DocumentAnalysisEntry {
  isProcessing?: boolean;
  lines: string[]
  analyzedAt: string
  fileKey: string
  validation?: {
    passed: boolean
    checks: DocumentValidationCheck[]
  }
  extractedData?: {
    powerDate?: string
    notaryName?: string
    notaryOffice?: string
    documentType?: string
    extractionContext?: {
      dateSource?: string
      notarySource?: string
      officeSource?: string
      typeSource?: string
    }
  }
  individualResults?: {
    originalName: string;
    lines: string[];
    extractedData?: any;
    validation?: any;
  }[];
}

export type DocumentAnalysis = Partial<Record<keyof TransferDocuments, DocumentAnalysisEntry>>

export interface TransferStatusEntry {
  status: TransferStatus
  timestamp: string
  note?: string
}

export interface Transfer {
  _id: string
  number: number
  organization: string
  profile: string
  salenote: string
  vehicle: string

  body: {
    organization: OrganizationBase
    client: ClientBase
    profile: {
      user: {
        firstname: string
        lastname: string
        nin: string
        phone?: string
      }
      email: string
    }
  }

  vehicleData: TransferVehicleData
  buyer: TransferParty
  seller: TransferParty
  compraPara?: CompraPara
  type: TransferType
  taxCalculationBase?: 'highest' | 'price' | 'appraisal'

  // Financial
  price: number
  fiscalAppraisal: number
  municipalTax: number
  civilRegistryFee: number
  transfieroFee: number
  veeklsCommission: number
  mercadoPagoFee: number
  totalCost: number

  // Documents
  documents: TransferDocuments

  // OCR results per document (from Textract)
  documentAnalysis?: DocumentAnalysis

  // Transfiero Online
  transfieroId?: string
  buyerFirmaCode?: string
  buyerFirmaLink?: string
  sellerFirmaCode?: string
  sellerFirmaLink?: string

  // Mercado Pago
  mercadoPagoPreferenceId?: string
  mercadoPagoPaymentId?: string

  // Status
  status: TransferStatus
  statusHistory: TransferStatusEntry[]

  // Timestamps
  createdAt: string
  updatedAt: string
  voidedAt?: string
  voidedBy?: string
  voidedCause?: string
}

export interface TransferInitDto {
  salenoteId: string
  fiscalAppraisal: number
  buyer: TransferParty
  seller: TransferParty
  compraPara?: CompraPara
  mercadoPagoFee: number
  organizationId: string
  type: TransferType
  taxCalculationBase?: 'highest' | 'price' | 'appraisal'
}

export interface TransferInitResponse {
  transfer: Transfer
  // initPoint and sandboxInitPoint removed in tolsimple (no Mercado Pago)
}

export interface TransfersListResponse {
  transfers: Transfer[]
  total: number
  page: number
  limit: number
}

// Status label map for UI display
export const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
  [TransferStatus.DRAFT]: 'Borrador',
  [TransferStatus.PENDING_DOCUMENTS]: 'Pendiente documentos',
  [TransferStatus.PENDING_PAYMENT]: 'Pendiente pago',
  [TransferStatus.PAID]: 'Pagado',
  [TransferStatus.SENT_TO_TRANSFIERO]: 'Enviado',
  [TransferStatus.IN_PROCESS]: 'En proceso',
  [TransferStatus.SIGNATURE_READY]: 'Listo para firma',
  [TransferStatus.NOTARIZED]: 'Notariado',
  [TransferStatus.REGISTERED]: 'Inscrito',
  [TransferStatus.REJECTED]: 'Rechazado',
  [TransferStatus.VOIDED]: 'Anulado',
}

// Status color map for badges
export const TRANSFER_STATUS_COLORS: Record<TransferStatus, string> = {
  [TransferStatus.DRAFT]: 'gray',
  [TransferStatus.PENDING_DOCUMENTS]: 'amber',
  [TransferStatus.PENDING_PAYMENT]: 'orange',
  [TransferStatus.PAID]: 'blue',
  [TransferStatus.SENT_TO_TRANSFIERO]: 'indigo',
  [TransferStatus.IN_PROCESS]: 'purple',
  [TransferStatus.SIGNATURE_READY]: 'teal',
  [TransferStatus.NOTARIZED]: 'green',
  [TransferStatus.REGISTERED]: 'emerald',
  [TransferStatus.REJECTED]: 'red',
  [TransferStatus.VOIDED]: 'red',
}
